import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Autocomplete,
  Box,
  FilterOptionsState,
  InputAdornment,
  IconButton,
  SxProps,
  Theme,
} from '@mui/material';
import { ReactComponent as CloseIcon } from '../../assets/close-2.svg';
import { ReactComponent as PlusIcon } from '../../assets/plus-2.svg';
import { distinctArray } from '../../utils/common';
import Button from '../Button';
import { Flex } from '../Flex';
import TextField from '../TextField';
import { space } from '../../style/utils';

const Wrapper = styled(Flex)`
  padding: ${space('sm', 0)};
  gap: ${space('sm', 'md')};
  align-items: flex-start;
  flex-direction: column;
`;

const TextFieldRow = styled(Flex)`
  align-items: center;
  width: 100%;
`;

const StyledTextField = styled(TextField)`
  flex: 1;
`;

const CloseIconStyled = styled(CloseIcon)`
  width: 12px;
  path {
    fill: ${({ theme }) => theme.palette.icon.subtle};
  }
`;

const AddButtonWrapper = styled(Flex)`
  width: 100%;
  justify-content: flex-start;
`;

export type Props = {
  sx?: SxProps<Theme>;
  value?: string[];
  onChange?: (val: string[]) => void;
  renderer?: (val: string) => React.ReactNode;
  optionRenderer?: (val: string) => React.ReactNode;
  buttonText: string;
  placeholder?: string;
  autoCompleteOptions?: string[];
  readOnly?: boolean;
};

const TextFieldList = React.forwardRef(
  (
    {
      sx,
      value = [],
      onChange,
      renderer,
      optionRenderer,
      buttonText,
      placeholder,
      autoCompleteOptions = [],
      readOnly,
    }: Props,
    ref: any,
  ) => {
    const [editMode, setEditMode] = useState<boolean>(false);
    const [typedValue, setTypedValue] = useState<string>('');

    const inputRef = useRef<any>();

    const distinctOptions = useMemo(
      () => distinctArray(autoCompleteOptions).sort(),
      [autoCompleteOptions],
    );

    const handleDelete = (index: number) => {
      if (value) {
        onChange?.(value.filter((_, i) => index !== i));
      }
    };

    const handleValueChange = (index: number, newValue: string) => {
      if (value) {
        const updatedValues = [...value];
        updatedValues[index] = newValue;
        onChange?.(updatedValues);
      }
    };

    const handleTextFieldKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === 'Escape') {
        e.preventDefault();
        (e.target as HTMLInputElement).blur();
      }
    };

    const handleKeyDown = (e: any) => {
      if (e.key === 'Escape') {
        setEditMode(false);
        setTypedValue('');
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        setEditMode(false);
      }
    };

    const handleChange = (_: any, newValue: string | null) => {
      const val = newValue?.trim();

      if (val && !value?.includes(val)) {
        onChange?.([...value, val]);
        setEditMode(false);
        setTypedValue('');
      }
    };

    const handleAddClick = () => {
      setEditMode(true);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    };

    const handleBlur = () => {
      const trimmedVal = typedValue.trim();
      if (trimmedVal.length > 0 && !value?.includes(trimmedVal)) {
        onChange?.([...value, trimmedVal]);
      }
      setEditMode(false);
      setTypedValue('');
    };

    const filterOptions = (
      options: string[],
      state: FilterOptionsState<string>,
    ) => {
      const val = state.inputValue.toLowerCase();
      const nonSelectedOptions = options.filter((o) => !value?.includes(o));

      const startsWith = nonSelectedOptions.filter((o) =>
        o.toLowerCase().startsWith(val),
      );

      const includes = nonSelectedOptions.filter(
        (o) =>
          o.toLowerCase().includes(val) &&
          !startsWith.find((s) => s.toLowerCase() === o.toLowerCase()),
      );

      return [...startsWith, ...includes];
    };

    return (
      <Wrapper sx={sx} aria-label="textfieldlist" ref={ref}>
        {value?.map((item, i) => (
          <TextFieldRow key={i}>
            <StyledTextField
              value={renderer ? renderer(item) : item}
              onChange={(e) => handleValueChange(i, e.target.value)}
              onKeyDown={handleTextFieldKeyDown}
              disabled={readOnly}
              InputProps={{
                endAdornment: !readOnly && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(i)}
                      edge="end"
                    >
                      <CloseIconStyled />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </TextFieldRow>
        ))}

        {editMode && !readOnly && (
          <TextFieldRow>
            <Autocomplete
              sx={{ flex: 1 }}
              freeSolo
              options={distinctOptions}
              onChange={handleChange}
              openOnFocus
              onInputChange={(e: any) => setTypedValue(e?.target.value || '')}
              inputValue={typedValue}
              placeholder={placeholder}
              onKeyDown={handleKeyDown}
              componentsProps={{ popper: { sx: { width: 'auto !important' } } }}
              onBlur={handleBlur}
              renderOption={
                optionRenderer
                  ? (props, option) => (
                      <Box component="li" {...props}>
                        {optionRenderer(option)}
                      </Box>
                    )
                  : undefined
              }
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              renderInput={({ size: _muiSize, ...params }) => (
                <TextField {...params} inputRef={inputRef} />
              )}
              filterOptions={filterOptions}
            />
          </TextFieldRow>
        )}

        {!editMode && !readOnly && (
          <AddButtonWrapper>
            <Button
              color="secondary"
              startIcon={<PlusIcon />}
              onClick={handleAddClick}
            >
              {buttonText}
            </Button>
          </AddButtonWrapper>
        )}
      </Wrapper>
    );
  },
);

export default TextFieldList;
